import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react'

export default function AdminPricing() {
  const { user } = useAuth()
  const [schoolId, setSchoolId] = useState(null)
  const [lessonTypes, setLessonTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [newForm, setNewForm] = useState({ label: '', description: '', price: '', hours_from: '08:00', hours_to: '17:00', available_from: '', available_to: '' })
  const [editForm, setEditForm] = useState({ label: '', description: '', price: '', hours_from: '08:00', hours_to: '17:00', available_from: '', available_to: '' })
  const [error, setError] = useState('')

  useEffect(() => {
    if (!user) return
    loadPricing()
  }, [user])

  const loadPricing = async () => {
    const { data: profile } = await supabase
      .from('profiles').select('school_id').eq('id', user.id).single()
    if (!profile?.school_id) { setLoading(false); return }
    setSchoolId(profile.school_id)
    const { data } = await supabase
      .from('lesson_pricing')
      .select('lesson_type, label, description, price, active, hours_from, hours_to, available_from, available_to')
      .eq('school_id', profile.school_id)
      .order('label', { ascending: true, nullsFirst: false })
    setLessonTypes(data || [])
    setLoading(false)
  }

  const slugify = (str) =>
    str.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

  const formatTime = (t) => {
    if (!t) return ''
    const [h, m] = t.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const hour = h % 12 || 12
    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`
  }

  const formatDate = (d) => {
    if (!d) return ''
    const [year, month, day] = d.split('-')
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    return `${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`
  }

  const handleAdd = async () => {
    setError('')
    if (!newForm.label.trim()) { setError('Name is required.'); return }
    if (!newForm.price || isNaN(newForm.price)) { setError('Valid price is required.'); return }
    setSaving(true)
    const lesson_type = slugify(newForm.label) || `type-${Date.now()}`
    const { error: err } = await supabase.from('lesson_pricing').upsert({
      school_id: schoolId,
      lesson_type,
      label: newForm.label.trim(),
      description: newForm.description.trim(),
      price: parseFloat(newForm.price) || 0,
      active: true,
      hours_from: newForm.hours_from || '08:00',
      hours_to: newForm.hours_to || '17:00',
      available_from: newForm.available_from || null,
      available_to: newForm.available_to || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'school_id,lesson_type' })
    if (err) { setError(err.message); setSaving(false); return }
    setNewForm({ label: '', description: '', price: '', hours_from: '08:00', hours_to: '17:00' })
    setShowAdd(false)
    await loadPricing()
    setSaving(false)
  }

  const handleEdit = (lt) => {
    setEditingId(lt.lesson_type)
    setEditForm({ label: lt.label || lt.lesson_type, description: lt.description || '', price: String(lt.price), hours_from: lt.hours_from || '08:00', hours_to: lt.hours_to || '17:00', available_from: lt.available_from || '', available_to: lt.available_to || '' })
  }

  const handleSaveEdit = async () => {
    setError('')
    if (!editForm.label.trim()) { setError('Name is required.'); return }
    if (!editForm.price || isNaN(editForm.price)) { setError('Valid price is required.'); return }
    setSaving(true)
    const { error: err } = await supabase.from('lesson_pricing').update({
      label: editForm.label.trim(),
      description: editForm.description.trim(),
      price: parseFloat(editForm.price) || 0,
      hours_from: editForm.hours_from,
      hours_to: editForm.hours_to,
      available_from: editForm.available_from || null,
      available_to: editForm.available_to || null,
      updated_at: new Date().toISOString(),
    }).eq('school_id', schoolId).eq('lesson_type', editingId)
    if (err) { setError(err.message); setSaving(false); return }
    setEditingId(null)
    await loadPricing()
    setSaving(false)
  }

  const handleToggleActive = async (lesson_type, currentActive) => {
    const newActive = currentActive === false ? true : false
    await supabase.from('lesson_pricing')
      .update({ active: newActive, updated_at: new Date().toISOString() })
      .eq('school_id', schoolId).eq('lesson_type', lesson_type)
    await loadPricing()
  }

  const handleDelete = async (lesson_type) => {
    if (!window.confirm('Delete this lesson type? Customers will no longer see it when booking.')) return
    setDeleting(lesson_type)
    await supabase.from('lesson_pricing').delete()
      .eq('school_id', schoolId).eq('lesson_type', lesson_type)
    setDeleting(null)
    await loadPricing()
  }

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
    </div>
  )

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', gap: 16 }}>
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 4 }}>
            Lesson Types & Pricing
          </h2>
          <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
            Create and manage lesson types for your school. Customers see these when booking.
          </p>
        </div>
        <button
          onClick={() => { setShowAdd(true); setError('') }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            background: 'var(--navy)', color: 'white', border: 'none',
            borderRadius: 10, padding: '10px 18px',
            fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          <Plus size={15} /> New Lesson Type
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '0.75rem 1rem', borderRadius: 8, marginBottom: 12, background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.25)', color: 'var(--coral)', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {/* Add new form */}
      {showAdd && (
        <div style={{
          background: 'white', border: '1.5px solid var(--teal)',
          borderRadius: 14, padding: '1.25rem', marginBottom: 12,
          boxShadow: '0 4px 20px rgba(14,170,164,0.1)',
        }}>
          <h4 style={{ margin: '0 0 1rem', fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.95rem' }}>
            New Lesson Type
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Name *</label>
              <input
                className="form-control"
                placeholder="e.g. Sunrise Session, Stand-Up Paddleboard..."
                value={newForm.label}
                onChange={e => setNewForm(f => ({ ...f, label: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Price ($) *</label>
              <input
                className="form-control"
                type="number" min="0" step="1" placeholder="75"
                value={newForm.price}
                onChange={e => setNewForm(f => ({ ...f, price: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Description <span style={{ color: 'var(--text-light)', fontWeight: 400 }}>(optional)</span></label>
            <input
              className="form-control"
              placeholder="Short description shown to customers..."
              value={newForm.description}
              onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Opens At</label>
              <input className="form-control" type="time" value={newForm.hours_from || '08:00'} onChange={e => setNewForm(f => ({ ...f, hours_from: e.target.value }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Closes At</label>
              <input className="form-control" type="time" value={newForm.hours_to || '17:00'} onChange={e => setNewForm(f => ({ ...f, hours_to: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginBottom: 8, fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-mid)' }}>
            Season dates <span style={{ fontWeight: 400, color: 'var(--text-light)' }}>(optional — leave blank for year-round)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Open From</label>
              <input className="form-control" type="date" value={newForm.available_from || ''} onChange={e => setNewForm(f => ({ ...f, available_from: e.target.value }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Open Until</label>
              <input className="form-control" type="date" value={newForm.available_to || ''} onChange={e => setNewForm(f => ({ ...f, available_to: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleAdd}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'var(--teal)', color: 'white', border: 'none',
                borderRadius: 8, padding: '9px 20px',
                fontSize: '0.85rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'DM Sans, sans-serif', opacity: saving ? 0.7 : 1,
              }}
            >
              <Check size={14} /> {saving ? 'Saving...' : 'Add Lesson Type'}
            </button>
            <button
              onClick={() => { setShowAdd(false); setNewForm({ label: '', description: '', price: '', hours_from: '08:00', hours_to: '17:00', available_from: '', available_to: '' }); setError('') }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'transparent', color: 'var(--text-mid)',
                border: '1px solid rgba(11,30,61,0.15)',
                borderRadius: 8, padding: '9px 16px',
                fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Lesson type list */}
      {lessonTypes.length === 0 && !showAdd ? (
        <div style={{
          textAlign: 'center', padding: '4rem 2rem',
          background: 'white', borderRadius: 14,
          border: '1px dashed rgba(11,30,61,0.15)',
        }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🏄</div>
          <p style={{ fontWeight: 700, color: 'var(--text-dark)', marginBottom: 6, fontSize: '1rem' }}>No lesson types yet</p>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '1.25rem' }}>
            Add your first lesson type so customers can start booking.
          </p>
          <button
            onClick={() => setShowAdd(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--navy)', color: 'white', border: 'none',
              borderRadius: 10, padding: '10px 20px',
              fontSize: '0.875rem', fontWeight: 700, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            <Plus size={15} /> New Lesson Type
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {lessonTypes.map(lt => (
            <div key={lt.lesson_type} style={{
              background: 'white',
              border: `1.5px solid ${editingId === lt.lesson_type ? 'var(--teal)' : 'rgba(11,30,61,0.1)'}`,
              borderRadius: 12,
              padding: '1rem 1.25rem',
              transition: 'border-color 0.2s',
            }}>
              {editingId === lt.lesson_type ? (
                /* ── Edit mode ── */
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 12 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Name</label>
                      <input
                        className="form-control"
                        value={editForm.label}
                        onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                        autoFocus
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Price ($)</label>
                      <input
                        className="form-control"
                        type="number" min="0" step="1"
                        value={editForm.price}
                        onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Description</label>
                    <input
                      className="form-control"
                      value={editForm.description}
                      onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Opens At</label>
                      <input className="form-control" type="time" value={editForm.hours_from || '08:00'} onChange={e => setEditForm(f => ({ ...f, hours_from: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Closes At</label>
                      <input className="form-control" type="time" value={editForm.hours_to || '17:00'} onChange={e => setEditForm(f => ({ ...f, hours_to: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 8, fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-mid)' }}>
                    Season dates <span style={{ fontWeight: 400, color: 'var(--text-light)' }}>(leave blank for year-round)</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Open From</label>
                      <input className="form-control" type="date" value={editForm.available_from || ''} onChange={e => setEditForm(f => ({ ...f, available_from: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Open Until</label>
                      <input className="form-control" type="date" value={editForm.available_to || ''} onChange={e => setEditForm(f => ({ ...f, available_to: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: 'var(--teal)', color: 'white', border: 'none',
                        borderRadius: 8, padding: '7px 16px',
                        fontSize: '0.825rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: 'DM Sans, sans-serif', opacity: saving ? 0.7 : 1,
                      }}
                    >
                      <Check size={13} /> {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => { setEditingId(null); setError('') }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: 'transparent', color: 'var(--text-mid)',
                        border: '1px solid rgba(11,30,61,0.15)',
                        borderRadius: 8, padding: '7px 14px',
                        fontSize: '0.825rem', fontWeight: 600, cursor: 'pointer',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      <X size={13} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* ── View mode ── */
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-dark)' }}>
                        {lt.label || lt.lesson_type}
                      </span>
                      {lt.active === false && (
                        <span style={{
                          fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px',
                          borderRadius: 50, background: 'rgba(11,30,61,0.08)',
                          color: 'var(--text-light)', letterSpacing: '0.02em',
                        }}>Inactive</span>
                      )}
                    </div>
                    {lt.description ? (
                      <div style={{ fontSize: '0.775rem', color: 'var(--text-light)', marginTop: 2 }}>
                        {lt.description}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.72rem', color: 'rgba(11,30,61,0.25)', marginTop: 2, fontStyle: 'italic' }}>
                        No description
                      </div>
                    )}
                    <div style={{ fontSize: '0.72rem', color: 'var(--teal)', marginTop: 3 }}>
                      ⏰ {formatTime(lt.hours_from || '08:00')} – {formatTime(lt.hours_to || '17:00')}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', marginTop: 2 }}>
                      📅 {lt.available_from && lt.available_to
                        ? `${formatDate(lt.available_from)} – ${formatDate(lt.available_to)}`
                        : 'Year-round'}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.15rem', fontWeight: 800,
                    color: 'var(--teal)', minWidth: 56, textAlign: 'right',
                  }}>
                    ${lt.price}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                    {/* Active/Inactive toggle pill */}
                    <button
                      onClick={() => handleToggleActive(lt.lesson_type, lt.active)}
                      title={lt.active === false ? 'Inactive — click to activate' : 'Active — click to deactivate'}
                      style={{
                        position: 'relative', width: 38, height: 22, borderRadius: 11,
                        border: 'none', cursor: 'pointer', padding: 0,
                        background: lt.active === false ? 'rgba(11,30,61,0.18)' : 'var(--teal)',
                        transition: 'background 0.2s', flexShrink: 0,
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: 3,
                        left: lt.active === false ? 3 : 17,
                        width: 16, height: 16, borderRadius: '50%',
                        background: 'white', transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        display: 'block',
                      }} />
                    </button>
                    <button
                      onClick={() => { handleEdit(lt); setError('') }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        background: 'rgba(11,30,61,0.05)',
                        border: '1px solid rgba(11,30,61,0.12)',
                        color: 'var(--text-mid)', borderRadius: 7,
                        padding: '6px 12px', fontSize: '0.775rem',
                        fontWeight: 600, cursor: 'pointer',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(lt.lesson_type)}
                      disabled={deleting === lt.lesson_type}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        background: 'rgba(232,97,74,0.07)',
                        border: '1px solid rgba(232,97,74,0.2)',
                        color: 'var(--coral)', borderRadius: 7,
                        padding: '6px 12px', fontSize: '0.775rem',
                        fontWeight: 600, cursor: deleting === lt.lesson_type ? 'not-allowed' : 'pointer',
                        fontFamily: 'DM Sans, sans-serif',
                        opacity: deleting === lt.lesson_type ? 0.5 : 1,
                      }}
                    >
                      <Trash2 size={12} /> {deleting === lt.lesson_type ? '...' : 'Delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {lessonTypes.length > 0 && (
        <p style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '1rem' }}>
          {lessonTypes.length} lesson type{lessonTypes.length !== 1 ? 's' : ''} · Customers see these in the booking flow.
        </p>
      )}
    </div>
  )
}
